import './Summary.scss';
import {
    AppId,
    Button,
    ButtonType,
    FCX,
    getFormattedThousand,
    Tooltip,
    usePlatformContext
} from "@datassential/platform-ui-lib";
import { IFoodProfileSummary } from "@models/NewFoodProfileSummary";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { FiDownload, FiExternalLink } from "react-icons/fi";
import { IconPackages } from "@icons";
import http from "@core/fetch";
import { sanitizeUrl } from "@helpers";
import { toast } from "react-toastify";
import cn from "classnames";
import PipelineMenu from "@pages/Food/components/MacStageMenu/MacStageMenu";
import { addFileToPackage } from "@actions/packages.actions";
import { useDispatch } from "react-redux";
import Chart from "@pages/Food/components/SummaryChart/SummaryChart";
import AutoSizer from "react-virtualized-auto-sizer";
import Tabs from "@pages/Food/components/ChartTabs/ChartTabs";
import { Link } from "react-router-dom";
import { buildFilteredChainsUrl } from "@pages/NewTopChains/PlacesFoodsTabs/PlacesFoodsTabs.helpers";
import PermissionOverlay from "@pages/Food/components/PermissionOverlay/PermissionOverlay";
import { PermissionLevelEnum } from "@models/PermissionSection";
import { requestTrialByFeature } from "@actions/page";
import AnimatedNumber from "@pages/Food/components/AnimatedNumber";
import { buildPenetrationChartData, pctValueFormatter, pctValueSignFormatter } from "@pages/Food/Food.helpers";

const ExternalLinkIcon: FCX<{
    size: number;
}> = ({
    size,
}) => (
    <FiExternalLink style={{
        width: size,
        height: size,
        marginLeft: 8,
        marginBottom: -2,
    }}/>
);

const FoodSummary: FCX<{
    data: IFoodProfileSummary;
}> = ({
    data: {
        id,
        name,
        data,
    },
}) => {
    const dispatch = useDispatch();
    const requestTrial: (f: string, hideNotification?: boolean) => any = (featureName, hideNotification) => {
        if (!hideNotification) {
            toast.info('Request has been sent');
        }
        return dispatch(requestTrialByFeature(featureName));
    };

    const {
        userInfo: { products }
    } = usePlatformContext();

    const mtProduct = products[AppId.MenuTrends];

    const [activeSliceIndex, setActiveSliceIndex] = useState<number>(0);
    const [isReportLoading, setIsReportLoading] = useState(false);
    const {
        filterDescription,
        chainsMoreCommon,
        consumerMostCommon,
        downloadLink,
        chainsCount,
        loveLikePct,
        consumerPreferencesPermissionLevel,
        slices,
        macStage,
        suggestedMacKeywords,
        cpLink,
        categoryName,
    } = data.data;

    const activeSlice = useMemo(
        () => slices[activeSliceIndex],
        [activeSliceIndex, slices]
    );

    const downloadReport = useCallback(
        () => {
            setIsReportLoading(true);
            http.downloadFileXHRFromUrl('GET', downloadLink)
                .then(() => {
                    toast.success('Report downloaded successfully');
                })
                .catch(() => {
                    toast.error('Failed to download report');
                })
                .finally(() => {
                    setIsReportLoading(false);
                });
        },
        [downloadLink]
    );

    const addToPackage = useCallback(() => {
        toast.success('Item has been added to package');
        dispatch(addFileToPackage({
            itemId: id,
            itemDocumentType: 'foodProfile',
        }));
    }, [dispatch, id]);

    const bulletsList1 = useMemo(
        (): ReactNode[] => {
            const result: ReactNode[] = [];

            if (activeSlice) {
                result.push(<>
                    on<b>&nbsp;
                    <AnimatedNumber
                        value={activeSlice.penetration}
                        valueFormatter={pctValueFormatter}
                    />
                    &nbsp;</b>of {activeSlice.name} menus
                </>);
                result.push(<>
                    historic growth: 12-month<b>&nbsp;
                    <AnimatedNumber
                        value={activeSlice.oneYearTrend}
                        valueFormatter={pctValueSignFormatter}
                    />
                    </b>,
                    4-year<b>&nbsp;
                    <AnimatedNumber
                        value={activeSlice.fourYearTrend}
                        valueFormatter={pctValueSignFormatter}
                    />
                    </b>
                </>);
                result.push(<>
                    predicted growth:
                    4-year<b>&nbsp;
                    <AnimatedNumber
                        value={activeSlice.fourYearFutureTrend}
                        valueFormatter={pctValueSignFormatter}
                    />
                    </b>
                </>);
            }

            if (chainsCount > 0) {
                result.push(
                    <div className="FoodSummary__bullet-inner">
                        <div>
                            on the menu at&nbsp;
                            <Link
                                to={buildFilteredChainsUrl(name)}
                                target={'_blank'}
                                data-testid="buttonFoodSummaryChainsExternalLink"
                            >
                                <b>{getFormattedThousand(chainsCount)} chains</b>
                                <ExternalLinkIcon size={20}/>
                            </Link>
                        </div>
                        <div className="FoodSummary__bullet-sub">
                            {chainsMoreCommon
                                ? (<>more common at {chainsMoreCommon} restaurants</>)
                                : 'found equally at chain and independent restaurants'
                            }
                        </div>
                    </div>
                );
            }

            return result;
        },
        [chainsCount, activeSlice, chainsMoreCommon, name]
    );

    const bulletsList2 = useMemo(
        (): ReactNode[] => {
            const result: ReactNode[] = [];

            if (loveLikePct > 0) {
                result.push(<>
                    <b>{Math.round(loveLikePct)}%&nbsp;</b>of consumers love or like it
                    {consumerPreferencesPermissionLevel === PermissionLevelEnum.Full && (
                        <a
                            href={cpLink}
                            target={'_blank'}
                            rel={'noreferrer'}
                            data-testid="buttonFoodSummaryLoveLikeExternalLink"
                        >
                            <ExternalLinkIcon size={20}/>
                        </a>
                    )}
                </>);
            }

            if (consumerMostCommon.length > 0) {
                result.push(<>most popular with<b>&nbsp;{consumerMostCommon.join(', ')}&nbsp;</b> consumers</>);
            } else {
                result.push('equally popular with all consumer types');
            }

            return result;
        },
        [loveLikePct, consumerMostCommon, consumerPreferencesPermissionLevel, cpLink]
    );

    const slicesItems = useMemo(
        () => {
            return slices.map(slice => ({
                id: slice.id,
                name: `${slice.name} ${pctValueFormatter(slice.penetration)}`,
            }));
        },
        [slices]
    );

    const chartData = useMemo(
        () => buildPenetrationChartData(slices),
        [slices]
    );

    const hasAccess = data.permissionLevel !== PermissionLevelEnum.None;

    return (
        <div
            className={cn(
                "FoodSummary appears-top",
                !hasAccess && 'is-blocked'
            )}
            data-testid="blockFoodSummary"
        >
            <div className="FoodSummary__aside">
                <div className="FoodSummary__aside-content">
                    <a
                        className="FoodSummary__title p appears-left del-1"
                        href={sanitizeUrl(`${mtProduct.url}${process.env.REACT_APP_MT_BYO_LINK}${encodeURIComponent(name)}`)}
                        target={'_blank'}
                        rel={'noreferrer'}
                        data-testid="buttonFoodSummaryTitleExternalLink"
                    >
                        {name}
                        {hasAccess && (
                            <div
                                data-tooltip-id="mt-link"
                                data-testid="buttonFoodSummaryTitleExternalLinkIcon"
                            >
                                <ExternalLinkIcon
                                    size={24}
                                />
                                <Tooltip
                                    id="mt-link"
                                    place="right-end"
                                >
                                    <div data-testid="textFoodSummaryTitleExternalLinkTooltip">
                                        Analyze <b>{name}</b> in Menu Trends
                                    </div>
                                </Tooltip>
                            </div>
                        )}
                    </a>
                    {filterDescription ? (
                        <div
                            className="p appears-left del-2 FoodSummary__blur"
                            data-testid="textFoodSummaryFilterDescription"
                        >
                            menu data filtered on {filterDescription}
                        </div>
                    ) : (<div className="p appears-left del-2">&nbsp;</div>)}
                    <ul className="FoodSummary__blur">
                        {bulletsList1.map((bullet, index) => (
                            <li
                                key={index}
                                className={`p appears-left del-${index + 2}`}
                                data-testid={`blockFoodSummaryBullet${index}`}
                            >
                                <div>{bullet}</div>
                            </li>
                        ))}
                    </ul>
                    {bulletsList1.length > 0 && bulletsList2.length > 0 && (
                        <div className={`FoodSummary__delimiter appears-left del-${bulletsList1.length + 2}`}/>
                    )}
                    <ul className="FoodSummary__blur">
                        {bulletsList2.map((bullet, index) => (
                            <li
                                key={index}
                                className={`p appears-left del-${index + bulletsList1.length + 3}`}
                                data-testid={`blockFoodSummaryBullet${index + bulletsList1.length}`}
                            >
                                <div>{bullet}</div>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="FoodSummary__aside-footer FoodSummary__blur">
                    <Button
                        type={ButtonType.Button}
                        onClick={downloadReport}
                        style={{ width: 160, }}
                        isDisabled={isReportLoading}
                        className={cn(
                            `appears-bottom del-${3 + bulletsList1.length + bulletsList2.length}`,
                            isReportLoading && 'FoodSummary__button-loading'
                        )}
                        data-testid="buttonFoodSummaryDownloadReport"
                    >
                        {!isReportLoading && (<>
                            Download <FiDownload style={{ marginLeft: '5px', width: 20, height: 20 }}/>
                        </>)}
                        {isReportLoading && 'loading...'}
                    </Button>
                    <Button
                        onClick={addToPackage}
                        type={ButtonType.Button}
                        style={{ width: 160, }}
                        className={cn(
                            `appears-bottom del-${4 + bulletsList1.length + bulletsList2.length}`,
                        )}
                        data-testid="buttonFoodSummaryAddToPackage"
                    >
                        Package
                        <IconPackages style={{
                            filter: 'brightness(0) saturate(100%)',
                            marginLeft: '5px',
                        }}/>
                    </Button>
                </div>
            </div>
            <div className="FoodSummary__main">
                <div className="FoodSummary__main-header appears-top del-10 FoodSummary__blur">
                    <PipelineMenu
                        macStage={macStage}
                        suggestedMacKeywords={suggestedMacKeywords}
                        categoryName={categoryName}
                    />
                </div>
                <div className="FoodSummary__main-content appears-right del-11">
                    <AutoSizer>
                        {({ width, height }) => chartData === null ? null : (
                            <Chart
                                width={width}
                                height={height}
                                {...chartData}
                                activeChartIndex={activeSliceIndex}
                                chartsTitles={slices.map(s => s.name)}
                                printValue={(v) => `${v.toFixed(1)}%`}
                            />
                        )}
                    </AutoSizer>
                </div>
                <div className="FoodSummary__main-footer appears-bottom del-10 FoodSummary__blur">
                    <Tabs
                        data={slicesItems}
                        activeItemIndex={activeSliceIndex}
                        setActiveItemIndex={setActiveSliceIndex}
                    />
                </div>
            </div>
            {!hasAccess && (
                <PermissionOverlay
                    onContactUsClick={() => requestTrial('mtd')}
                    testId="blockFoodSummaryPermissionOverlay"
                >
                    <div data-testid="textFoodSummaryPermissionOverlayContentLine-0">
                        Upgrade your subscription to unlock
                    </div>
                    <div data-testid="textFoodSummaryPermissionOverlayContentLine-1">
                        insights about <b>{name}</b>
                    </div>
                </PermissionOverlay>
            )}
        </div>
    );
}

export default FoodSummary;